import React, { useRef, useEffect } from 'react';

export interface CustomDataViewColumn {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: any) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface CustomDataViewProps {
  columns: CustomDataViewColumn[];
  data: any[];
  onSort?: (key: string) => void;
  sortKey?: string;
  sortOrder?: 'asc' | 'desc' | null;
  emptyMessage?: string;
  className?: string;
}

/**
 * CustomDataView - A plain table-like data display component
 * Displays data in table structure without styling
 * But maintains sorting and other data manipulation features
 */
export const CustomDataView: React.FC<CustomDataViewProps> = ({
  columns,
  data,
  onSort,
  sortKey = '',
  sortOrder = null,
  emptyMessage = 'No data available',
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to top and left when data changes or sort is applied
  useEffect(() => {
    if (containerRef.current) {
      // Find the scrollable parent (check both vertical and horizontal)
      let scrollableParent = containerRef.current.parentElement;
      while (scrollableParent) {
        const hasVerticalScroll =
          scrollableParent.scrollHeight > scrollableParent.clientHeight;
        const hasHorizontalScroll =
          scrollableParent.scrollWidth > scrollableParent.clientWidth;
        const computedStyle = window.getComputedStyle(scrollableParent);
        const overflowY = computedStyle.overflowY;
        const overflowX = computedStyle.overflowX;
        const isVerticallyScrollable =
          hasVerticalScroll && (overflowY === 'auto' || overflowY === 'scroll');
        const isHorizontallyScrollable =
          hasHorizontalScroll &&
          (overflowX === 'auto' || overflowX === 'scroll');

        if (isVerticallyScrollable || isHorizontallyScrollable) {
          scrollableParent.scrollTop = 0;
          scrollableParent.scrollLeft = 0;
          break;
        }
        scrollableParent = scrollableParent.parentElement;
      }
    }
  }, [data, sortKey, sortOrder]);

  const handleSort = (key: string, sortable?: boolean) => {
    if (sortable && onSort) {
      onSort(key);
    }
  };

  const getSortIcon = (key: string, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortKey !== key) {
      return (
        <span className="ml-1 inline-flex flex-col">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="currentColor"
            viewBox="0 0 12 12"
          >
            <path d="M6 4L3 7h6L6 4z" />
          </svg>
          <svg
            className="w-5 h-5 text-gray-600 -mt-1.5"
            fill="currentColor"
            viewBox="0 0 12 12"
          >
            <path d="M6 8L3 5h6L6 8z" />
          </svg>
        </span>
      );
    }

    if (sortOrder === 'asc') {
      return (
        <span className="ml-1 inline-flex">
          <svg
            className="w-5 h-5 text-gray-700"
            fill="currentColor"
            viewBox="0 0 12 12"
          >
            <path d="M6 3L3 7h6L6 3z" />
          </svg>
        </span>
      );
    }

    if (sortOrder === 'desc') {
      return (
        <span className="ml-1 inline-flex">
          <svg
            className="w-5 h-5 text-gray-700"
            fill="currentColor"
            viewBox="0 0 12 12"
          >
            <path d="M6 9L3 5h6L6 9z" />
          </svg>
        </span>
      );
    }

    return null;
  };

  // Calculate minimum width based on column widths
  const calculateMinWidth = () => {
    let totalWidth = 0;
    columns.forEach(column => {
      if (column.width) {
        // Extract numeric value from width (e.g., "150px" -> 150)
        const numericWidth = parseInt(column.width.replace(/\D/g, ''), 10);
        totalWidth += numericWidth || 100; // Default to 100 if parsing fails
      } else {
        // For flex columns without explicit width, assume minimum 150px
        totalWidth += 150;
      }
    });
    return totalWidth;
  };

  const minWidth = calculateMinWidth();

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        border: '1px solid #ddd',
        minWidth: `${minWidth}px`,
        width: '100%',
      }}
    >
      {/* Header Row */}
      <div
        style={{
          display: 'flex',
          borderBottom: '2px solid #000',
          backgroundColor: '#DDF0FF',
        }}
      >
        {columns.map((column, colIndex) => (
          <div
            key={column.key}
            onClick={() => handleSort(column.key, column.sortable)}
            style={{
              cursor: column.sortable ? 'pointer' : 'default',
              flex: column.width ? `0 0 ${column.width}` : '1',
              minWidth: column.width || '100px',
              maxWidth: column.width || 'auto',
              width: column.width || 'auto',
              padding: '8px',
              fontWeight: 'bold',
              textAlign: column.align || 'left',
              borderRight:
                colIndex < columns.length - 1 ? '1px solid #a9b8ceff' : 'none',
              boxSizing: 'border-box',
              wordWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: '1.2',
              display: 'flex',
              alignItems: 'center',
              justifyContent:
                column.align === 'right'
                  ? 'flex-end'
                  : column.align === 'center'
                  ? 'center'
                  : 'flex-start',
            }}
          >
            <span>{column.label}</span>
            {getSortIcon(column.key, column.sortable)}
          </div>
        ))}
      </div>

      {/* Data Rows */}
      {data.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {emptyMessage}
        </div>
      ) : (
        data.map((item, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              display: 'flex',
              borderBottom:
                rowIndex < data.length - 1 ? '1px solid #ddd' : 'none',
            }}
          >
            {columns.map((column, colIndex) => (
              <div
                key={column.key}
                style={{
                  flex: column.width ? `0 0 ${column.width}` : '1',
                  minWidth: column.width || '50px',
                  maxWidth: column.width || 'auto',
                  width: column.width || 'auto',
                  padding: '8px',
                  textAlign: column.align || 'left',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  borderRight:
                    colIndex < columns.length - 1 ? '1px solid #ddd' : 'none',
                  boxSizing: 'border-box',
                }}
              >
                {column.render ? column.render(item) : item[column.key] ?? '-'}
              </div>
            ))}
          </div>
        ))
      )}

      {/* Footer */}
      {/* {data.length > 0 && (
        <div style={{ borderTop: '2px solid #000', padding: '8px' }}>
          Showing {data.length} {data.length === 1 ? 'item' : 'items'}
        </div>
      )} */}
    </div>
  );
};

export default CustomDataView;
